import {Point} from "../models/point";

type Cartesian = { x: number; y: number; z: number };

export class LocationHelper {
  private static EARTH_RADIUS = 6371000; // Earth's radius in meters

  /**
   * Smooths the list of locations using a weighted moving average based on accuracy.
   * Client-side implementation, as synchronization in app requires client-side smoothing.
   */
  smoothPoints(locations: Point[], windowSize: number): Point[] {
    const smoothedPoints: Point[] = [];

    for (let i = 0; i < locations.length; i++) {
      const start = Math.max(0, i - windowSize);
      const end = Math.min(locations.length - 1, i + windowSize);

      let sumX = 0;
      let sumY = 0;
      let sumZ = 0;
      let totalWeight = 0;

      for (let j = start; j <= end; j++) {
        const location = locations[j];
        const cartesian = this.geographicToCartesian(location);
        const accuracy = location.accuracy ?? 1;
        const weight = 1 / (accuracy * accuracy);

        sumX += cartesian.x * weight;
        sumY += cartesian.y * weight;
        sumZ += cartesian.z * weight;
        totalWeight += weight;
      }

      const avgX = sumX / totalWeight;
      const avgY = sumY / totalWeight;
      const avgZ = sumZ / totalWeight;

      const smoothedCartesian = { x: avgX, y: avgY, z: avgZ };
      smoothedPoints.push(this.cartesianToGeographic(smoothedCartesian));
    }

    return smoothedPoints;
  }

  /**
   * Converts geographic coordinates (lat, lng) to Cartesian coordinates (x, y, z).
   */
  private geographicToCartesian(location: Point): Cartesian {
    const latRad = this.toRadians(location.lat);
    const lngRad = this.toRadians(location.lng);

    const x = LocationHelper.EARTH_RADIUS * Math.cos(latRad) * Math.cos(lngRad);
    const y = LocationHelper.EARTH_RADIUS * Math.cos(latRad) * Math.sin(lngRad);
    const z = LocationHelper.EARTH_RADIUS * Math.sin(latRad);

    return { x, y, z };
  }

  /**
   * Converts Cartesian coordinates (x, y, z) back to geographic (lat, lng).
   */
  private cartesianToGeographic(cartesian: Cartesian): Point {
    const lat = this.toDegrees(Math.asin(cartesian.z / LocationHelper.EARTH_RADIUS));
    const lng = this.toDegrees(Math.atan2(cartesian.y, cartesian.x));

    return { lat, lng, accuracy: 0.0 }; // Accuracy is irrelevant after smoothing
  }

  /**
   * Converts degrees to radians.
   */
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Converts radians to degrees.
   */
  private toDegrees(radians: number): number {
    return radians * (180 / Math.PI);
  }
}
